/* global mainScript */
import React, { useState } from "react";
import PropTypes from "prop-types";
import styled from "@emotion/styled";
import { motion } from "framer-motion";

import SortDropDown from "./SortDropDown";

// style rules
import { Colors, Sizes, Animation } from "../styles";

const Filters = ({
	filters,
	selectFilter,
	selectedFilter,
	isRides,
	selectSortOption,
	selectedSortOption,
	isFood
}) => {
	const [isViewMoreSelected, setIsViewMoreSelected] = useState(false);

	const onViewMore = (e) => {
		e.target.blur();
		return setIsViewMoreSelected((currentValue) => !currentValue);
	};

	// we only display filters when theres more than 2, to avoid something like
	// All - Single Filter
	let ShowFilters = filters && filters.length > 2;

	// only show the view more button when more than 5 filters are present on the page
	let ShowViewMoreButton = filters && filters.length > 5;

	const renderedFilters =
		ShowFilters &&
		filters.map((filter) => {
			return (
				<Filter
					variants={Animation.cards.opacityVariants}
					initial={"initial"}
					animate={"animate"}
					key={filter.slug}
					active={selectedFilter.slug == filter.slug}
					onClick={() => selectFilter(filter)}
					onKeyPress={() => selectFilter(filter)}
					isRides={isRides}
					isAll={filter.slug == "all"}
				>
					{filter.name}
				</Filter>
			);
		});

	const renderViewMoreButton = ShowViewMoreButton && (
		<ViewMoreFilters
			variants={Animation.cards.opacityVariants}
			initial={"initial"}
			animate={"animate"}
			tabIndex={0}
			onClick={onViewMore}
			onKeyPress={onViewMore}
			isRides={isRides}
			icon={
				isViewMoreSelected
					? mainScript.assetPath + "images/svg/icons/minus.svg"
					: mainScript.assetPath + "images/svg/icons/plus2.svg"
			}
		>
			{isViewMoreSelected ? "view less" : "view more"}
		</ViewMoreFilters>
	);

	return (
		<FiltersContainer isRides={isRides}>
			{!isRides && ShowFilters && <Title>Filter by</Title>}
			<FiltersWrapper ShowFilters={ShowFilters}>
				<Row>
					{ShowViewMoreButton && !isViewMoreSelected
						? renderedFilters.splice(0, 4)
						: renderedFilters}
					{renderViewMoreButton}
				</Row>
				{!isRides && (
					<SortDropDown
						selectSortOption={selectSortOption}
						selectedSortOption={selectedSortOption}
						isFood={isFood}
					/>
				)}
			</FiltersWrapper>
		</FiltersContainer>
	);
};

export default Filters;

const FiltersContainer = styled.div`
	padding-top: 5rem;

	@media only screen and (min-width: ${Sizes.screens.large}) {
		padding-top: ${(props) => (props.isRides ? "0" : "5rem")};
		width: 100%;
	}
`;

const Title = styled.h2`
	font-size: 2rem;
	line-height: 2.6rem;
	letter-spacing: -0.0015em;
`;

const FiltersWrapper = styled.div`
	display: flex;
	padding-top: 2rem;
	flex-direction: column;

	@media only screen and (min-width: ${Sizes.screens.large}) {
		flex-direction: row;
		justify-content: ${(props) =>
		props.ShowFilters ? "flex-start" : "flex-end"};
	}
`;

const Row = styled.div`
	display: flex;
	gap: 3rem;
	flex-wrap: wrap;
	flex: 1;
	align-items: center;
`;

const Filter = styled(motion.button)`
	display: flex;
	justify-content: center;
	align-items: center;
	font-size: 1.3rem;
	background: ${(props) =>
		props.active ? Colors.cneAreas.limeGreen : "transparent"};
	background-size: 0% 100%;
	border: 1px solid
		${(props) =>
		props.active
			? Colors.cneAreas.limeGreen
			: props.isRides
				? Colors.base.white
				: Colors.base.black};
	color: ${(props) =>
		props.active
			? Colors.base.black
			: props.isRides
				? Colors.base.white
				: Colors.base.black};
	padding: ${(props) => (props.isAll ? "1.5rem 4.8rem" : "1.5rem")};
	transition: all 0.7s;

	${(props) =>
		!props.active &&
		`&:hover,
		 &:focus {
			border: 1px solid ${Colors.cneAreas.limeGreen} ;
			color: black;
			background: transparent;
			background-image: ${Colors.gradients.green};
			background-repeat: no-repeat;
			background-size: 200% 100%;
			transition: all .7s;
		}`}
`;

const ViewMoreFilters = styled(Filter)`
	gap: 1rem;

	&:after {
		height: 24px;
		content: url(${props => props.icon});
		transition: all 0.5s ease-in;
	}

	&:hover {
		&:after {
			transition: all 0.5s ease-in;
			transform: rotate(180deg);
		}
	}
`;

Filters.propTypes = {
	filters: PropTypes.array,
	selectFilter: PropTypes.func,
	selectedFilter: PropTypes.object,
	isRides: PropTypes.bool,
	setSelectedFilter: PropTypes.func,
	selectSortOption: PropTypes.func,
	selectedSortOption: PropTypes.string,
	isFood: PropTypes.bool
};
